import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { submitContact, fetchSettings } from '@/services/api';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchSettings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    const phone = settings.contact_phone || '+86-137-9029-6151';
    const email = settings.contact_email || 'andy-zou@qzyunxin.com';
    const address = settings.contact_address || "No.6 Sunshine Road, Xiamei Town, Nan'an City, Fujian Province, China";
    const companyName = settings.company_name || 'Quanzhou Yunxin Outdoor Products Co., Ltd.';

    const contactSchema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${SITE_URL}/contact`,
      url: `${SITE_URL}/contact`,
      name: 'Contact Us | YUNXIN OUTDOOR',
      description: `Contact ${companyName} for product inquiries, OEM/ODM orders, and partnership opportunities.`,
      mainEntity: {
        '@type': 'Organization',
        name: companyName,
        telephone: phone,
        email: email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: address,
          addressCountry: 'CN',
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: phone,
          contactType: 'sales',
          email: email,
          availableLanguage: ['English', 'Chinese'],
        },
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.text = JSON.stringify(contactSchema);
    document.head.appendChild(script);
    document.title = 'Contact Us | YUNXIN OUTDOOR — Get a Quote';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `Contact ${companyName} for tool bag and organizer bag inquiries. Phone: ${phone}`);
    return () => {
      const el = document.getElementById('schema-contact');
      if (el) el.remove();
    };
  }, [settings]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) { setError('Message must be under 500 characters.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await submitContact(form);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败，请稍后再试');
    } finally {
      setSubmitting(false);
    }
  };

  const companyName = settings.company_name || 'Quanzhou Yunxin Outdoor Products Co., Ltd.';
  const address = settings.contact_address || "No.6 Sunshine Road, Xiamei Town, Nan'an City, Fujian Province, China";
  const phone = settings.contact_phone || '+86 137 9029 6151';
  const phoneHref = 'tel:' + phone.replace(/[\s()-]/g, '');
  const email1 = settings.contact_email || 'andy-zou@qzyunxin.com';
  const email2 = settings.contact_email2 || '375974361@qq.com';

  const contactItems = [
    { icon: 'ri-building-line', label: 'Company', value: companyName },
    { icon: 'ri-map-pin-line', label: 'Address', value: address },
    { icon: 'ri-phone-line', label: 'Phone', value: phone, href: phoneHref },
    { icon: 'ri-mail-line', label: 'Email', value: email1, href: `mailto:${email1}` },
  ];
  if (email2) {
    contactItems.push({ icon: 'ri-mail-line', label: 'Email 2', value: email2, href: `mailto:${email2}` });
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-stone-900">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-4 block">Contact Us</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-4">
            Let&apos;s Work<br />Together
          </h1>
          <p className="text-stone-400 text-base max-w-xl leading-relaxed">
            Whether you&apos;re looking to place an order, explore OEM options, or simply learn more about our products — we&apos;re here to help.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-bold text-stone-900 mb-8">Send Us a Message</h2>
              {submitted ? (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-10 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-amber-700 text-2xl" />
                  </div>
                  <h3 className="text-stone-900 font-bold text-xl mb-2">Message Sent!</h3>
                  <p className="text-stone-500 text-sm">Thank you for reaching out. Our team will get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold tracking-wider uppercase mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold tracking-wider uppercase mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold tracking-wider uppercase mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 8900"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold tracking-wider uppercase mb-2">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                        className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold tracking-wider uppercase mb-2">Message * <span className="text-stone-400 normal-case font-normal">({form.message.length}/500)</span></label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us about your requirements, quantity, or any questions..."
                      className="w-full border border-stone-200 rounded-lg px-4 py-3 text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-stone-400 transition-colors resize-none"
                    />
                    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-stone-900 hover:bg-stone-700 text-white py-4 rounded-full text-sm font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-stone-900 mb-8">Contact Information</h2>
              {contactItems.map((item) => (
                <div key={item.label} className="flex gap-4 items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                    <i className={`${item.icon} text-amber-700`} />
                  </div>
                  <div>
                    <p className="text-stone-400 text-xs tracking-wider uppercase mb-1">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} rel="nofollow" className="text-stone-900 text-sm hover:text-amber-700 transition-colors">{item.value}</a>
                    ) : (
                      <p className="text-stone-900 text-sm leading-relaxed">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* Map */}
              <div className="rounded-2xl overflow-hidden h-56 mt-6">
                <iframe
                  title="Yunxin Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.5!2d118.38!3d24.95!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDU3JzAwLjAiTiAxMTjCsDIyJzQ4LjAiRQ!5e0!3m2!1sen!2scn!4v1680000000000!5m2!1sen!2scn"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
